import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Tabs } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import "./TimeSelectModal.css";
import Image from "next/image";
import { useRecState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { SearchStateType } from "../../../Orchestrator/Types/Index";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { _eventBus } from "../../../core/events/EventBus";
import { useIntl } from "react-intl";
import { IFmlmFilter, IFmlmView } from "../../../Orchestrator/Types/Interface/IFmlmView";
import { fmlmUpdateFilterAction } from "../../../Actions/UIActions/FmlmViewInformation/FmlmUpdateFilterAction";
import { evtTree } from "../../../Orchestrator/ConstantTree";
import { DEFAULT_TIMEZONE } from "../../../config/global";
import { fmlmViewInfoTree } from "../../../Orchestrator/ConstantTree/FmlmSearch/FmlmViewInformationTree";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { DEFAULT_FMLM_FILTER_STATE } from "./FmlmFilterDrawer";

dayjs.extend(utc);
dayjs.extend(timezone);

const { TabPane } = Tabs;

export const MAX_FLEX_HOURS = 2;

interface IProps {
  open: boolean;
  isFromPopUp: boolean;
  fmlmFilterState: IFmlmFilter;
  setFmlmFilterState: React.Dispatch<React.SetStateAction<IFmlmFilter | null>>;
  trainType: keyof typeof TrainTypeEnum;
  handleOk?: (fmlmFilter: IFmlmFilter) => void;
}

const TimeSelectModalContent = (props: IProps) => {
  const { isFromPopUp, fmlmFilterState, setFmlmFilterState, handleOk, trainType, open } = props;
  const [search] = useRecState<SearchStateType>("searchStateAtom");
  const [searchPopupData] = useRecState<SearchStateType>("searchStatePopupAtom");
  const [selectedJourney] = useRecState<IFmlmView>("selectedFmlmJourneyAtom");

  const intl = useIntl();

  const updateFilterState = (data: Partial<IFmlmFilter>) => {
    const newFmlmFilter = {
      ...fmlmFilterState,
      ...data
    }

    setFmlmFilterState(newFmlmFilter)
  }

  const handleHour = (delta: number) => updateFilterState({ hour: ((fmlmFilterState.hour) + delta + 24) % 24 });
  const handleMinute = (delta: number) => updateFilterState({ minute: ((fmlmFilterState.minute) + delta + 60) % 60 });
  // const handleFlexHr = (delta: number) => {
  //   const newFlexHr: 1 | 2 = Math.min(MAX_FLEX_HOURS, Math.max(1, fmlmFilterState.flexHr + delta)) as 1 | 2;
  //   if (newFlexHr !== fmlmFilterState.flexHr) {
  //     updateFilterState({ flexHr: newFlexHr });
  //   }
  // };

  // Check if selected time is in the past (Asia/Dubai)
  const isTimeInPast = useMemo(() => {
    if (trainType === 'inbound') {
      let inboundDate = search.inboundDate;
      if (isFromPopUp) {
        inboundDate = searchPopupData.inboundDate;
      }
      if (!inboundDate) return false;

      const fmlmSearchTime = dayjs(inboundDate).tz(DEFAULT_TIMEZONE, true).hour(fmlmFilterState.hour).minute(fmlmFilterState.minute).second(0).millisecond(0);
      return fmlmSearchTime.isBefore(dayjs(selectedJourney.arrivalTimestampPlanned).tz(DEFAULT_TIMEZONE).tz(DEFAULT_TIMEZONE));
    } else {
      let outboundDate = search.outboundDate;
      if (isFromPopUp) {
        outboundDate = searchPopupData.outboundDate;
      }
      if (!outboundDate) return false;
      const fmlmSearchTime = dayjs(outboundDate).tz(DEFAULT_TIMEZONE, true).hour(fmlmFilterState.hour).minute(fmlmFilterState.minute).second(0).millisecond(0);
      return fmlmSearchTime.isBefore(dayjs().subtract(1, "minute").tz(DEFAULT_TIMEZONE));
    }
  }, [search.outboundDate, searchPopupData.outboundDate, fmlmFilterState.hour, fmlmFilterState.minute, trainType, open]);

  const handleContinue = () => {
    let fmlmFilterPayload = { ...fmlmFilterState };
    if (isFromPopUp) {
      fmlmFilterPayload.sortBy = DEFAULT_FMLM_FILTER_STATE.sortBy;
      fmlmFilterPayload.originTransportMode = DEFAULT_FMLM_FILTER_STATE.originTransportMode;
      fmlmFilterPayload.destinationTransportMode = DEFAULT_FMLM_FILTER_STATE.destinationTransportMode;
    }

    _eventBus.emit({
      eventName: fmlmViewInfoTree.Filter.UpdateFilter,
      data: fmlmFilterPayload
    });

    if (handleOk) {
      handleOk(fmlmFilterPayload);
      return false
    }
    const isHomePage = window.location.pathname === "/search";

    _eventBus.emit({
      eventName: evtTree.SearchBar.TimeSelectModal.Click,
      data: {
        search: search,
        searchPopupData: searchPopupData,
        fmlmFilter: fmlmFilterPayload,
        isTimerVisible: false,
        isFromPopUp: isFromPopUp,
        trainType: isHomePage ? TrainTypeEnum.outbound : trainType
      },
    })
  };

  return (
    <div className="time-modal-container">
      <div className="time-modal-icon">
        <ClockCircleOutlined className="color-primary" />
      </div>
      <div className="time-modal-instruction ff-inter">
        {intl.formatMessage({ id: "relatedJourney" })}
      </div>
      <div className="time-modal-divider" />
      <div className="time-modal-label-row">
        <span className="time-modal-label ff-inter">
          {intl.formatMessage({ id: "selectTime" })}
        </span>
        <Tabs
          activeKey={fmlmFilterState.mode}
          onChange={(key) => updateFilterState({ mode: key as "leave" | "arrive" })}
          className="time-modal-tabs"
          tabBarGutter={4}
        >
          <TabPane
            tab={
              <span className={`ff-inter time-modal-tab${fmlmFilterState.mode === "leave" ? " active" : ""}`}>
                {intl.formatMessage({ id: "leaveAt" })}
              </span>
            }
            key="leave"
          />
          <TabPane
            tab={
              <span className={`ff-inter time-modal-tab${fmlmFilterState.mode === "arrive" ? " active" : ""}`}>
                {intl.formatMessage({ id: "arriveBy" })}
              </span>
            }
            key="arrive"
          />
        </Tabs>
      </div>
      <div className="time-modal-picker-card" dir="ltr">
        <div className="time-modal-picker-row three-arrows">
          <Button
            className="time-modal-arrow"
            onClick={() => handleHour(1)}
            icon={<Image src="/assets/fmlm/angle-up.svg" alt="up" width={24} height={24} />}
          />
          <Button
            className="time-modal-arrow"
            onClick={() => handleMinute(1)}
            icon={<Image src="/assets/fmlm/angle-up.svg" alt="up" width={24} height={24} />}
          />
          {/* <Button
            className="time-modal-arrow hour-window"
            onClick={() => handleFlexHr(1)}
            icon={<Image src="/assets/fmlm/angle-up.svg" alt="up" width={24} height={24} />}
          /> */}
        </div>
        <div className="time-modal-time-row time-modal-pill-bg">
          <span className="time-modal-time">{(fmlmFilterState.hour).toString().padStart(2, "0")}</span>
          <span className="time-modal-colon">:</span>
          <span className="time-modal-time">{(fmlmFilterState.minute).toString().padStart(2, "0")}</span>
          {/* <span className="time-modal-flex hour-window">+/- {fmlmFilterState.flexHr} {intl.formatMessage({ id: "hr" })}</span> */}
        </div>
        <div className="time-modal-picker-row three-arrows">
          <Button
            className="time-modal-arrow"
            onClick={() => handleHour(-1)}
            icon={<Image src="/assets/fmlm/angle-down.svg" alt="down" width={24} height={24} />}
          />
          <Button
            className="time-modal-arrow"
            onClick={() => handleMinute(-1)}
            icon={<Image src="/assets/fmlm/angle-down.svg" alt="down" width={24} height={24} />}
          />
          {/* <Button
            className="time-modal-arrow hour-window"
            onClick={() => handleFlexHr(-1)}
            icon={<Image src="/assets/fmlm/angle-down.svg" alt="down" width={24} height={24} />}
          /> */}
        </div>
      </div>
      <Button
        className={`time-modal-continue-btn ff-inter${isTimeInPast ? " disabled-btn" : ""}`}
        type="primary"
        disabled={isTimeInPast}
        onClick={handleContinue}
      >
        {intl.formatMessage({ id: "continue" })}
      </Button>
    </div>
  );
};


const TimeSelectModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  isFromPopUp?: boolean;
  trainType: keyof typeof TrainTypeEnum;
  handleOk?: (IFmlmFilter: IFmlmFilter) => void;
}> = (props) => {
  const { open, handleClose, isFromPopUp = false, handleOk, trainType } = props

  const [isDepartureDateChangedAtom] = useRecState<boolean>("isDepartureDateChangedAtom");
  const [fmlmFilter] = useRecState<IFmlmFilter>("fmlmFilterAtom");
  const [search] = useRecState<SearchStateType>("searchStateAtom");
  const [searchPopup] = useRecState<SearchStateType>("searchStatePopupAtom");
  const [selectedJourney] = useRecState<IFmlmView>("selectedFmlmJourneyAtom");
  const [fmlmFilterState, setFmlmFilterState] = useState<IFmlmFilter | null>(null);

  useEffect(() => {
    if (open) {
      const currentTime = dayjs().tz(DEFAULT_TIMEZONE);

      // Opens when inbound date select first time
      if (isFromPopUp === false && trainType === TrainTypeEnum.inbound) {
        let newFmlmFilter = { ...fmlmFilter };
        let isFutureDate = dayjs(search.outboundDate).isAfter(dayjs(), "day");
        const isSameDay = dayjs(search.outboundDate).isSame(dayjs(search.inboundDate), "day");

        if (isSameDay) {
          let journeyEndtime = dayjs(selectedJourney.arrivalTimestampPlanned).tz(DEFAULT_TIMEZONE);
          newFmlmFilter.hour = journeyEndtime.hour();
          newFmlmFilter.minute = journeyEndtime.minute();

        } else if (isFutureDate) {
          newFmlmFilter.hour = 8;
          newFmlmFilter.minute = 0;
        } else {
          // This case never happens but added for safety
          newFmlmFilter.hour = currentTime.hour();
          newFmlmFilter.minute = currentTime.minute();
        }

        setFmlmFilterState(newFmlmFilter)
      } else if (isDepartureDateChangedAtom) {
        let journeyDate = trainType === TrainTypeEnum.inbound ? search.inboundDate : search.outboundDate;
        let newFmlmFilter = { ...fmlmFilter };
        if (isFromPopUp) {
          journeyDate = trainType === TrainTypeEnum.inbound ? searchPopup.inboundDate : searchPopup.outboundDate;
        }

        const isFutureDate = dayjs(journeyDate).isAfter(dayjs(), "day");

        if (isFutureDate) {
          newFmlmFilter.hour = 8;
          newFmlmFilter.minute = 0;
        } else {
          newFmlmFilter.hour = currentTime.hour();
          newFmlmFilter.minute = currentTime.minute();
        }

        setFmlmFilterState(newFmlmFilter)
      } else {
        const isTodaysDate = dayjs(search.outboundDate).isSame(dayjs(), "day");
        if (isTodaysDate) {
          let isPastTime = dayjs(search.outboundDate).set("hour", fmlmFilter.hour).set("minute", fmlmFilter.minute).isBefore(dayjs(), "minute");
          if (isTodaysDate && isPastTime) {
            let newFmlmFilter = { ...fmlmFilter };
            newFmlmFilter.hour = currentTime.hour();
            newFmlmFilter.minute = currentTime.minute();

            setFmlmFilterState(newFmlmFilter)
          } else {
            setFmlmFilterState(fmlmFilter);
          }
        } else {
          setFmlmFilterState(fmlmFilter);
        }
      }
    } else {
      setFmlmFilterState(null)
    }
  }, [open]);

  return (
    <Modal
      open={open}
      width={437}
      footer={null}
      mask={{ closable: true }}
      closable={false}
      className="search-modal"
      onCancel={handleClose}
    >
      {
        fmlmFilterState && (
          <TimeSelectModalContent
            open={open}
            isFromPopUp={isFromPopUp}
            fmlmFilterState={fmlmFilterState}
            setFmlmFilterState={setFmlmFilterState}
            handleOk={handleOk}
            trainType={trainType}
          />
        )
      }
    </Modal>
  );
};

export default TimeSelectModal;
